import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";

import { translateFr as tfr } from "@/i18n";
export const METRICS = [
  { id: "performance_score", label: "Athlynx Performance Score" },
  { id: "interactions", label: "Interactions" },
  { id: "meetings", label: "Rendez-vous" },
  { id: "mandates", label: tfr("Mandats signés") },
  { id: "placements", label: "Placements" },
  { id: "conversion_rate", label: "Taux de conversion" },
  { id: "followups", label: tfr("Relances effectuées") },
];

export const RANGE_PRESETS = [
  { id: "today", label: tfr("Aujourd'hui") },
  { id: "yesterday", label: tfr("Hier") },
  { id: "7d", label: tfr("7 derniers jours") },
  { id: "30d", label: tfr("30 derniers jours") },
  { id: "90d", label: tfr("90 derniers jours") },
  { id: "month", label: tfr("Mois en cours") },
  { id: "prev_month", label: tfr("Mois précédent") },
  { id: "quarter", label: tfr("Trimestre en cours") },
  { id: "6m", label: tfr("6 derniers mois") },
  { id: "12m", label: tfr("12 derniers mois") },
  { id: "year", label: tfr("Année en cours") },
  { id: "all", label: "Tout l'historique" },
];

export const COMPARE_OPTIONS = [
  { id: "previous", label: tfr("Période précédente") },
  { id: "last_year", label: tfr("Même période l'an dernier") },
  { id: "custom", label: tfr("Comparaison personnalisée") },
  { id: "none", label: tfr("Aucune comparaison") },
];

const cache = new Map();

/** Central time-driven state: one URL-synced range/compare/metric -> one coordinated fetch. */
export default function usePerformance() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const abortRef = useRef(null);

  const state = useMemo(() => ({
    range: params.get("range") || (params.get("from") ? null : "30d"),
    from: params.get("from"),
    to: params.get("to"),
    compare: params.get("compare") || "previous",
    compareFrom: params.get("compareFrom"),
    compareTo: params.get("compareTo"),
    metric: params.get("metric") || "performance_score",
    tab: params.get("tab") || "overview",
  }), [params]);

  const setState = useCallback((patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "" ||
          (k === "range" && v === "30d" && !patch.from) ||
          (k === "compare" && v === "previous") ||
          (k === "metric" && v === "performance_score") ||
          (k === "tab" && v === "overview")) next.delete(k);
      else next.set(k, v);
    });
    if (patch.range) { next.delete("from"); next.delete("to"); }
    if (patch.from) next.delete("range");
    if (patch.compare && patch.compare !== "custom") { next.delete("compareFrom"); next.delete("compareTo"); }
    setParams(next, { replace: true });
  }, [params, setParams]);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    if (state.range) q.set("range", state.range);
    if (state.from) q.set("dfrom", state.from);
    if (state.to) q.set("dto", state.to);
    q.set("compare", state.compare);
    if (state.compareFrom) q.set("compareFrom", state.compareFrom);
    if (state.compareTo) q.set("compareTo", state.compareTo);
    q.set("metric", state.metric);
    return q.toString();
  }, [state]);

  const load = useCallback(async (force = false) => {
    if (!force && cache.has(query)) {
      setData(cache.get(query));
      setLoading(false);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const r = await api.get(`/performance/overview?${query}`, { signal: ctrl.signal });
      cache.set(query, r.data);
      setData(r.data);
      setError(false);
    } catch (e) {
      if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") setError(true);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  return { state, setState, data, loading, error, reload: () => load(true) };
}
